export const GAME_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export const SURFACE_OPTIONS = [
  { value: 'hub_tile', label: 'Hub tile' },
  { value: 'feature', label: 'Feature' },
];

export const KNOWN_FEATURE_KEYS = {
  super_over_daily: { label: 'Super Over Daily', seedMin: '2.3.2' },
  super_over_league: { label: 'Super Over League', seedMin: '2.3.7' },
};

export const emptyForm = () => ({
  game_key: '',
  surface: 'hub_tile',
  title: '',
  image_url: '',
  navigation_link: '',
  sort_order: '0',
  min_app_version: '',
  max_app_version: '',
  is_enabled: true,
});

export const rowToForm = (row) => ({
  game_key: row?.game_key || '',
  surface: row?.surface || 'hub_tile',
  title: row?.title || '',
  image_url: row?.image_url || '',
  navigation_link: row?.navigation_link || '',
  sort_order: row?.sort_order != null ? String(row.sort_order) : '0',
  min_app_version: row?.min_app_version || '',
  max_app_version: row?.max_app_version || '',
  is_enabled: row?.is_enabled !== false,
});

/** Normalize "2.4" → "2.4.0" for display / compare (matches API behavior). */
export const normalizeVersion = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parts = trimmed.split('.').map((p) => p.trim());
  if (parts.some((p) => p === '' || !/^\d+$/.test(p))) return null;
  while (parts.length < 3) parts.push('0');
  return parts.slice(0, 3).join('.');
};

export const parseVersionParts = (value) => {
  const normalized = normalizeVersion(value);
  if (!normalized) return null;
  return normalized.split('.').map((n) => Number(n));
};

/** Compare a ≥ b → positive, a < b → negative, equal → 0. */
export const compareVersions = (a, b) => {
  const pa = parseVersionParts(a);
  const pb = parseVersionParts(b);
  if (!pa || !pb) return null;
  for (let i = 0; i < 3; i += 1) {
    if (pa[i] !== pb[i]) return pa[i] - pb[i];
  }
  return 0;
};

/**
 * Visibility formula (same as backend):
 * enabled AND (min null OR app ≥ min) AND (max null OR app < max)
 * Missing/unparseable app_version: hide if min set, else show (still respects max).
 */
export const isRowVisibleForAppVersion = (row, appVersionInput) => {
  if (!row?.is_enabled) return false;

  const min = normalizeVersion(row.min_app_version);
  const max = normalizeVersion(row.max_app_version);
  const appParts = parseVersionParts(appVersionInput);
  const hasApp = !!appParts;

  if (!hasApp) {
    if (min) return false;
    if (max) return true; // no app version → treated as old; max still applies only when parseable
    return true;
  }

  if (min) {
    const cmpMin = compareVersions(appVersionInput, min);
    if (cmpMin == null || cmpMin < 0) return false;
  }
  if (max) {
    const cmpMax = compareVersions(appVersionInput, max);
    if (cmpMax == null || cmpMax >= 0) return false;
  }
  return true;
};

export const formatVersionWindow = (row) => {
  const min = normalizeVersion(row?.min_app_version) || null;
  const max = normalizeVersion(row?.max_app_version) || null;
  if (!min && !max) return 'Always on';
  if (min && !max) return `≥ ${min}`;
  if (!min && max) return `< ${max}`;
  return `${min} ≤ v < ${max}`;
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const validateForm = (form, { isCreate }) => {
  const errors = {};

  if (isCreate) {
    const key = (form.game_key || '').trim();
    if (!key) errors.game_key = 'game_key is required';
    else if (!GAME_KEY_PATTERN.test(key)) {
      errors.game_key = 'Must match [a-z][a-z0-9_]{0,63}';
    }
  }

  if (!['hub_tile', 'feature'].includes(form.surface)) {
    errors.surface = 'surface must be hub_tile or feature';
  }

  if (!(form.title || '').trim()) errors.title = 'title is required';

  if (form.surface === 'hub_tile') {
    if (!(form.image_url || '').trim()) errors.image_url = 'image_url required for hub_tile';
    if (!(form.navigation_link || '').trim()) {
      errors.navigation_link = 'navigation_link required for hub_tile';
    }
  }

  const sortRaw = String(form.sort_order ?? '').trim();
  if (sortRaw !== '' && !/^-?\d+$/.test(sortRaw)) {
    errors.sort_order = 'sort_order must be an integer';
  }

  const minRaw = String(form.min_app_version ?? '').trim();
  const maxRaw = String(form.max_app_version ?? '').trim();
  const minNorm = minRaw ? normalizeVersion(minRaw) : null;
  const maxNorm = maxRaw ? normalizeVersion(maxRaw) : null;

  if (minRaw && !minNorm) errors.min_app_version = 'Invalid version (e.g. 2.4.0)';
  if (maxRaw && !maxNorm) errors.max_app_version = 'Invalid version (e.g. 2.4.0)';

  if (minNorm && maxNorm) {
    const cmp = compareVersions(maxNorm, minNorm);
    if (cmp == null || cmp <= 0) {
      errors.max_app_version = 'max must be strictly above min';
    }
  }

  return errors;
};

/** Build POST/PATCH body. Empty version strings become null (clear floor/ceiling). */
export const buildPayload = (form, { isCreate }) => {
  const body = {
    surface: form.surface,
    title: form.title.trim(),
    sort_order: Number.parseInt(String(form.sort_order || '0'), 10) || 0,
    is_enabled: !!form.is_enabled,
    min_app_version: String(form.min_app_version || '').trim() || null,
    max_app_version: String(form.max_app_version || '').trim() || null,
  };

  if (isCreate) {
    body.game_key = form.game_key.trim();
  }

  if (form.surface === 'hub_tile') {
    body.image_url = form.image_url.trim();
    body.navigation_link = form.navigation_link.trim();
  } else {
    // Feature rows may still carry optional image/link if provided
    const image = String(form.image_url || '').trim();
    const link = String(form.navigation_link || '').trim();
    body.image_url = image || null;
    body.navigation_link = link || null;
  }

  return body;
};

export const normalizeItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};
